import * as React from "react";
import { TextField, MenuItem } from "@mui/material";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

/**
 * Presenta un campo `integer` con `.choices(unit="…")` como un dropdown del
 * rango discreto [min..max]. El dato sigue siendo `number | null` (sin enum,
 * sin migración, aritmética intacta); solo cambia el control de UI.
 *
 * - El valor `null`/vacío se ofrece como una opción especial etiquetada con
 *   `nullText` ("Sin límite" / "Sin renovación") cuando el campo es nullable.
 * - Cada número N se etiqueta "N {unidad}", con singular para 1 y plural para
 *   el resto (unitSingular/unitPlural derivados de "singular|plural").
 */
type InputIntegerChoicesProps = {
  label: string;
  value: number | undefined | null;
  onChange: (value: number | undefined) => void;
  min: number;
  max: number;
  unitSingular?: string;
  unitPlural?: string;
  nullable?: boolean;
  nullText?: string;
  helperText?: string;
  error?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  description?: string;
  customWidth?: number;
};

const NULL_OPTION = "__null__";

export const InputIntegerChoices: React.FC<InputIntegerChoicesProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  unitSingular = "",
  unitPlural = "",
  nullable = false,
  nullText,
  helperText = "",
  error = false,
  autoFocus = false,
  disabled = false,
  hidden = false,
  description,
  customWidth = 500,
}) => {
  if (hidden) return null;

  const optionLabel = (n: number): string => {
    const unit = n === 1 ? unitSingular : unitPlural;
    return unit ? `${n} ${unit}` : `${n}`;
  };

  const numbers: number[] = [];
  for (let n = min; n <= max; n++) numbers.push(n);

  const isEmpty = value === undefined || value === null;
  const selectValue = isEmpty ? (nullable ? NULL_OPTION : "") : String(value);

  return (
    <TextField
      select
      size="small"
      sx={{ m: 1, mb: 1.5, width: customWidth }}
      label={<FieldLabelWithHelp label={label} description={description} />}
      InputLabelProps={{ shrink: true }}
      value={selectValue}
      error={error}
      helperText={helperText}
      autoFocus={autoFocus}
      disabled={disabled}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === NULL_OPTION || v === "" ? undefined : Number(v));
      }}
    >
      {nullable && (
        <MenuItem value={NULL_OPTION}>
          <em>{nullText ?? "—"}</em>
        </MenuItem>
      )}
      {numbers.map((n) => (
        <MenuItem key={n} value={String(n)}>
          {optionLabel(n)}
        </MenuItem>
      ))}
    </TextField>
  );
};
